import { useEffect, useRef, useState, type ReactNode } from "react";

/** Minimal modal used for naming and confirmations (no window.prompt: testable, styled, keyboard friendly). */
export function Dialog({
  title,
  children,
  onClose,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="dialog-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dialog" role="dialog" aria-modal="true" aria-label={title}>
        <h3>{title}</h3>
        {children}
      </div>
    </div>
  );
}

export function NameDialog({
  title,
  initial,
  hint,
  confirmLabel,
  onSubmit,
  onClose,
}: {
  title: string;
  initial: string;
  hint?: string;
  confirmLabel: string;
  onSubmit: (value: string) => Promise<void>;
  onClose: () => void;
}) {
  const [value, setValue] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const el = input.current;
    if (!el) return;
    el.focus();
    // Select the name without the extension, like VS Code.
    const slash = initial.lastIndexOf("/") + 1;
    const dot = initial.lastIndexOf(".");
    el.setSelectionRange(slash, dot > slash ? dot : initial.length);
  }, [initial]);

  return (
    <Dialog title={title} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          onSubmit(value.trim())
            .then(onClose)
            .catch((err: unknown) => setError((err as Error).message))
            .finally(() => setBusy(false));
        }}
      >
        <input
          ref={input}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          data-testid="name-input"
        />
        {hint && <p className="muted small">{hint}</p>}
        {error && (
          <p className="error small" data-testid="dialog-error">
            {error}
          </p>
        )}
        <div className="row end">
          <button type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="primary" disabled={busy || value.trim() === ""} data-testid="dialog-confirm">
            {confirmLabel}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

export function ConfirmDialog({
  title,
  message,
  confirmLabel,
  danger,
  onConfirm,
  onClose,
}: {
  title: string;
  message: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  onConfirm: () => Promise<void>;
  onClose: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <Dialog title={title} onClose={onClose}>
      <div className="dialog-body">{message}</div>
      {error && (
        <p className="error small" data-testid="dialog-error">
          {error}
        </p>
      )}
      <div className="row end">
        <button type="button" onClick={onClose} autoFocus>
          Cancel
        </button>
        <button
          className={danger ? "danger" : "primary"}
          disabled={busy}
          data-testid="dialog-confirm"
          onClick={() => {
            setBusy(true);
            onConfirm()
              .then(onClose)
              .catch((err: unknown) => setError((err as Error).message))
              .finally(() => setBusy(false));
          }}
        >
          {confirmLabel}
        </button>
      </div>
    </Dialog>
  );
}
